import React, { useState, useEffect, useReducer, useRef, useContext } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import { makeStyles } from "@material-ui/core/styles";
import { CircularProgress } from "@material-ui/core";
import clsx from "clsx";

import openSocket from "../../services/socket-io";
import whatsBackground from "../../assets/wa-background.png";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflowX: "visible",
    overflowY: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1
  },
  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 0 20px 20px",
    overflowY: "scroll",
    direction: "rtl",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px"
    },
    ...theme.scrollbarStyles
  },
  circleLoading: {
    color: theme.palette.primary.main,
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12
  },
  messageLeft: {
    direction: "ltr",
    alignSelf: "flex-start",
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "flex",
    alignItems: "flex-end",
    position: "relative"
  },
  messageLeftContent: {
    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 5,
    paddingBottom: 5,
    boxShadow: "0 1px 1px #b3b3b3",
    marginLeft: 8,
    position: "relative"
  },
  messageRight: {
    direction: "ltr",
    alignSelf: "flex-start",
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    position: "relative"
  },
  messageRightContent: {
    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 5,
    paddingBottom: 5,
    boxShadow: "0 1px 1px #b3b3b3",
    marginRight: 0,
    position: "relative"
  },
  senderName: {
    fontWeight: 500,
    fontSize: 12,
    color: "#075E54",
    marginBottom: 4
  },
  textContentItem: {
    overflowWrap: "break-word"
  },
  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 2,
    right: 8,
    color: "#999",
    backgroundColor: "inherit"
  },
  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3"
  },
  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px"
  },
  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderRadius: 8,
    marginBottom: 4
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach(message => {
      const index = state.findIndex(m => m.id === message.id);
      if (index !== -1) {
        state[index] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const message = action.payload;
    const index = state.findIndex(m => m.id === message.id);
    if (index !== -1) {
      state[index] = message;
    } else {
      state.push(message);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const InternalMessagesList = ({ chatId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();
  const currentChatId = useRef(chatId);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    currentChatId.current = chatId;
  }, [chatId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get(`/internal-chats/${chatId}/messages`, {
            params: { pageNumber }
          });

          if (currentChatId.current === chatId) {
            let messagesListDiv = document.getElementById("internalMessagesList");
            let prevScrollHeight = messagesListDiv ? messagesListDiv.scrollHeight : 0;
            let prevScrollTop = messagesListDiv ? messagesListDiv.scrollTop : 0;

            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);

            if (pageNumber === 1 && data.messages.length > 1) {
              scrollToBottom();
            } else if (pageNumber > 1 && messagesListDiv) {
              setTimeout(() => {
                if (messagesListDiv) {
                  messagesListDiv.scrollTop =
                    messagesListDiv.scrollHeight - prevScrollHeight + prevScrollTop;
                }
              }, 0);
            }
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [pageNumber, chatId]);

  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    const join = () => socket.emit("joinInternalChat", String(chatId));
    if (socket.connected) join();
    socket.on("connect", join);

    const onMessage = data => {
      if (data.action === "create" && data.message.internalChatId === Number(chatId)) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }
      if (data.action === "update" && data.message.internalChatId === Number(chatId)) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
      }
    };
    socket.on("internalMessage", onMessage);

    return () => {
      socket.off("connect", join);
      socket.off("internalMessage", onMessage);
      socket.emit("leaveInternalChat", String(chatId));
    };
  }, [chatId]);

  const loadMore = () => {
    setPageNumber(prev => prev + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = e => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("internalMessagesList").scrollTop = 1;
    }

    if (loading) return;

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      const messageDay = parseISO(messagesList[index].createdAt);
      const previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span className={classes.dailyTimestamp} key={`timestamp-${message.id}`}>
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderMessages = () => {
    if (messagesList.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: 24, color: "#808888" }}>
          {i18n.t("internalChat.noChatMessage")}
        </div>
      );
    }

    return messagesList.map((message, index) => {
      const fromMe = message.userId === user.id;
      const createdAt = parseISO(message.createdAt);

      const content = (
        <div
          className={clsx(
            fromMe ? classes.messageRightContent : classes.messageLeftContent
          )}
        >
          {message.nameUser && (
            <div className={classes.senderName}>{message.nameUser}</div>
          )}
          {message.mediaUrl && (
            <img
              src={message.mediaUrl}
              alt="media"
              className={classes.messageMedia}
            />
          )}
          <div className={classes.textContentItem}>{message.body}</div>
          <span className={classes.timestamp}>
            {format(createdAt, "H:mm")}
          </span>
        </div>
      );

      return (
        <React.Fragment key={message.id}>
          {renderDailyTimestamps(message, index)}
          <div className={fromMe ? classes.messageRight : classes.messageLeft}>
            {content}
          </div>
        </React.Fragment>
      );
    });
  };

  return (
    <div className={classes.messagesListWrapper}>
      <div
        id="internalMessagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {renderMessages()}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default InternalMessagesList;

